import React, { useCallback, useRef, useMemo, useEffect, useState } from "react"
import PropTypes from "prop-types"
import { AgGridReact } from "ag-grid-react"
import { useDeepCompareMemo, useDeepCompareEffect } from "use-deep-compare"

// Components
import ExportMenu from "Components/ExportMenu"

// Style
import { TableContainer } from "./index.style"

// Utils
import {
  indexDisplayConfigs,
  DisplayModeCell,
  makeDisplayModeValueFormatter,
} from "./displayModeHelper"
import { onFitColumnsWidth, getColumnsProps, getColumnsSumRow, getConfiguredFooterRow } from "./utils"
import { exportCSV, exportPNG, exportTable, exportXLSX } from "./export"
import { useRemoteTranslation } from "Utils/useRemoteTranslation"
import { buildAgGridLocale } from "Utils/agGridLocale"

export default function AgTable({
  // Data
  dataChart,
  columnsLabelsMapping,
  displayModeValue,
  // Props
  languageId,
  freezeFirstColumn,
  numberDecimal,
  showSpaceForThousand,
  enableFilters,
  enableExportMenu,
  enablePagination,
  nbRowsPerPage,
  widgetTitle,
  controledRef,
  enableCountRows,
  enableColumnsSumFooter,
  footerAggregations,
  footerLabel,
  footerDecimals,
  // Style
  bodyEvenRowBackgroundColor,
  bodyOddRowBackgroundColor,
  bodyTableAlignement,
  headBackgroundColor,
  headCellJustifyContent,
  headColor,
  headFontSize,
  headUppercase,
  resizableColumns,
  enableTooltipCell,
  configFieldPersonalization,
}) {
  // Refs & Ids
  const containerRef = useRef()
  const gridRef = controledRef ? controledRef : useRef()
  const [tableId] = useState(`tableId${Math.random()}`)
  const [exportMenuId] = useState(`exportMenuId${Math.random()}`)
  const [initialNbRowsPerPage] = useState(nbRowsPerPage)
  const trad = useRemoteTranslation(languageId) || {}

  // Memos Options
  const localeText = useMemo(() => buildAgGridLocale(trad), [trad])

  const columnOptions = useMemo(
    () => ({
      enableCountRows,
      enableFilters,
      numberDecimal,
      showSpaceForThousand,
      freezeFirstColumn,
    }),
    [enableCountRows, enableFilters, numberDecimal, showSpaceForThousand, freezeFirstColumn]
  )

  const defaultColOptions = useMemo(
    () => ({
      minWidth: 150,
      filter: enableFilters,
      sortable: true,
      resizable: resizableColumns,
    }),
    [enableFilters, resizableColumns]
  )

  // Memos Styles
  const exportMenuPosition = { top: "0px", right: "25px" }
  const gridStyle = { height: "100%", width: "100%" }
  const pdfExportStyles = { fillColor: headBackgroundColor }

  const containerStyle = useMemo(
    () => ({
      bodyEvenRowBackgroundColor,
      bodyOddRowBackgroundColor,
      bodyTableAlignement,
      headBackgroundColor,
      headCellJustifyContent,
      headColor,
      headFontSize,
      headUppercase,
    }),
    [
      bodyEvenRowBackgroundColor,
      bodyOddRowBackgroundColor,
      bodyTableAlignement,
      headBackgroundColor,
      headCellJustifyContent,
      headColor,
      headFontSize,
      headUppercase,
    ]
  )

  const CustomTooltip = props => {
    const { value } = props
    return (
      <div
        style={{
          padding: 8,
          background: "#222",
          color: "#fff",
          borderRadius: 6,
          boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
          fontSize: 12,
          maxWidth: 450,
          lineHeight: 1.3,
          whiteSpace: "pre-wrap",
        }}
      >
        {value}
      </div>
    )
  }
  // Memos Data
  const data = useDeepCompareMemo(() => dataChart, [dataChart])
  // const columns = useDeepCompareMemo(
  //   () => getColumnsProps(columnsLabelsMapping, columnOptions),
  //   [columnsLabelsMapping, columnOptions]
  // )
  const displayConfigByUuid = useMemo(
    () => indexDisplayConfigs(displayModeValue),
    [displayModeValue]
  )

  const columns = useDeepCompareMemo(() => {
    const base = getColumnsProps(columnsLabelsMapping, columnOptions)

    return base.map(col => {
      const field = col.uuid
      const config = displayConfigByUuid.get(String(field))
      const configField = configFieldPersonalization?.find(config => config.idField === field)

      if (!config) {
        return {
          ...col,
          ...(enableTooltipCell && {
            tooltipValueGetter: p => (p.value != null ? String(p.value) : ""),
            tooltipComponent: "customTooltip",
          }),
          headerTooltip: col.headerName,
        }
      }

      return {
        ...col,
        cellRenderer: DisplayModeCell,
        cellRendererParams: { config, configField },
        // Optionnel : un valueFormatter pour export CSV/XLSX lisible
        valueFormatter: makeDisplayModeValueFormatter(config),
        // Optionnel : centrer le chip si tu veux
        cellClass: (col.cellClass ? [col.cellClass] : []).concat("ag-cell-center"),
        ...(enableTooltipCell && {
          tooltipValueGetter: p => {
            const vf = makeDisplayModeValueFormatter(config)
            const out = vf?.(p)
            return out != null ? String(out) : p.value ?? ""
          },
          tooltipComponent: "customTooltip",
        }),
        headerTooltip: col.headerName,
      }
    })
  }, [columnsLabelsMapping, displayConfigByUuid, columnOptions, enableTooltipCell, configFieldPersonalization])
  // Effects

  /** Change number rows per page when pagination is enabled */
  useEffect(() => {
    if (gridRef?.current?.api && enablePagination) {
      gridRef?.current?.api.paginationSetPageSize(nbRowsPerPage)
    }
  }, [nbRowsPerPage])

  /** Make columns fit to container when size change or when data changed */
  useDeepCompareEffect(() => {
    if (gridRef?.current !== undefined) {
      onFitColumnsWidth(gridRef?.current)
    }
  }, [
    containerRef?.current?.offsetHeight,
    containerRef?.current?.offsetWidth,
    columnsLabelsMapping,
  ])

  /** A function that is called when the grid is ready to be used. */
  const onGridReady = useCallback(() => {
    onFitColumnsWidth(gridRef?.current)
  }, [])

  //** Pinned bottoms rows, one object = one row */
  // Pied configuré colonne par colonne (`footerAggregations`) s'il est déclaré, sinon le pied
  // historique qui somme toutes les colonnes numériques — inchangé pour les tableaux existants.
  const columnsSumRow =
    footerAggregations && Object.keys(footerAggregations).length > 0
      ? getConfiguredFooterRow(columns, data, { aggregations: footerAggregations, label: footerLabel }, footerDecimals ?? numberDecimal, showSpaceForThousand)
      : getColumnsSumRow(columns, data, numberDecimal, showSpaceForThousand)
  const pinnedBottomRows = enableColumnsSumFooter ? [columnsSumRow] : []

  //** Set style for specials rows, here rowPinned */
  const getRowStyle = params => {
    if (params?.node?.rowPinned) {
      return { fontWeight: "bold" }
    }
  }

  const gridOptions = {
    getContextMenuItems: params => {
      // Obtenir les éléments par défaut
      const defaultItems = params.defaultItems
      // Filtrer pour retirer "export"
      return defaultItems?.filter(item => item !== "export")
    },
  }

  return (
    <TableContainer className="ag-theme-alpine" id={tableId} ref={containerRef} {...containerStyle}>
      {enableExportMenu && (
        <ExportMenu
          exportMenuPosition={exportMenuPosition}
          exportMenuId={exportMenuId}
          exportCSV={() => exportCSV(gridRef?.current, widgetTitle)}
          exportPNG={() => exportPNG(widgetTitle, tableId, document.getElementById(exportMenuId))}
          exportXLSX={() => exportXLSX(gridRef?.current, widgetTitle)} //exportTable(columns, data, "xlsx", widgetTitle)}
          exportPDF={() => exportTable(columns, data, "pdf", widgetTitle, pdfExportStyles)}
        />
      )}
      <AgGridReact
        rowSelection="multiple"
        style={gridStyle}
        ref={gridRef}
        localeText={localeText}
        rowData={data}
        defaultColDef={defaultColOptions}
        columnDefs={columns}
        pagination={enablePagination}
        paginationPageSize={initialNbRowsPerPage}
        onGridReady={onGridReady}
        pinnedBottomRowData={pinnedBottomRows}
        getRowStyle={getRowStyle}
        gridOptions={gridOptions}
        components={{ customTooltip: CustomTooltip }}
        tooltipShowDelay={200}
        tooltipHideDelay={100000}
        tooltipMouseTrack={true}
        enableBrowserTooltips={false}
      />
    </TableContainer>
  )
}

AgTable.defaultProps = {
  // Data
  dataChart: [],
  columnsLabelsMapping: [],
  // Props
  numberDecimal: 0,
  showSpaceForThousand: true,
  enableFilters: true,
  enableExportMenu: false,
  enablePagination: false,
  nbRowsPerPage: 100,
  enableCountRows: false,
  enableColumnsSumFooter: false,
  // Style
  bodyEvenRowBackgroundColor: "#FFFFFF",
  bodyOddRowBackgroundColor: "#FCFCFC",
  headBackgroundColor: "#F8F8F8",
  headColor: "#000000",
  headFontSize: "0.8rem",
  headCellJustifyContent: "center",
  bodyTableAlignement: "center",
  headUppercase: false,
  resizableColumns: false,
}

AgTable.propTypes = {
  // Data
  dataChart: PropTypes.array,
  columnsLabelsMapping: PropTypes.array,
  // Props
  numberDecimal: PropTypes.number,
  showSpaceForThousand: PropTypes.bool,
  enableExportMenu: PropTypes.bool,
  enableFilters: PropTypes.bool,
  enablePagination: PropTypes.bool,
  nbRowsPerPage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  enableCountRows: PropTypes.bool,
  enableColumnsSumFooter: PropTypes.bool,
  /** `{ [champ de colonne]: "sum" | "mean" }` — pied calculé sur ces seules colonnes. */
  footerAggregations: PropTypes.objectOf(PropTypes.oneOf(["sum", "mean"])),
  /** Libellé du pied configuré, écrit dans la première colonne. */
  footerLabel: PropTypes.string,
  /** Décimales du pied configuré — les cellules gardent `numberDecimal` (un compte n'a pas de décimale). */
  footerDecimals: PropTypes.number,
  // Style
  bodyEvenRowBackgroundColor: PropTypes.string,
  bodyOddRowBackgroundColor: PropTypes.string,
  bodyTableAlignement: PropTypes.string,
  headColor: PropTypes.string,
  headFontSize: PropTypes.string,
  headBackgroundColor: PropTypes.string,
  headCellJustifyContent: PropTypes.string,
  headUppercase: PropTypes.bool,
  resizableColumns: PropTypes.bool,
}
